/**
 * DonationPortalPage — Standalone Public Donation Site
 * ABMHC: Archbishop Bartholomew Manjoro Health Centre
 * No auth required — public-facing with shareable URLs
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Heart, Building2, Users, Clock, MapPin, Mail, Phone, ChevronRight,
  Target, Activity, CheckCircle2, Calendar, ArrowRight, Menu, X,
  Image as ImageIcon, Play, DollarSign, Stethoscope, Baby, Microscope,
  Pill, Shield, TrendingUp, ExternalLink
} from 'lucide-react';

import { submitDonation } from '@/lib/donationSubmit';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TENANT_ID = '69d63c2c39ded375827179d9';

const pubApi = async (path) => {
  const res = await fetch(`${API_URL}/api/donations/public/${TENANT_ID}${path}`);
  if (!res.ok) throw new Error('Request failed');
  return res.json();
};

// ==================== NAV ====================
const PortalNav = ({ currentSection, setSection, settings }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'progress', label: 'Progress' },
    { id: 'gallery', label: 'Gallery' },
    { id: 'donate', label: 'Donate', highlight: true },
  ];
  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200" data-testid="portal-nav">
      <div className="max-w-6xl mx-auto px-4 flex items-center justify-between h-14">
        <button onClick={() => setSection('home')} className="flex items-center gap-2">
          {settings?.logo_url ? (
            <img src={settings.logo_url} alt="Logo" className="h-8 w-auto object-contain" />
          ) : (
            <Building2 className="h-6 w-6 text-blue-700" />
          )}
          <span className="font-bold text-blue-900 text-sm hidden sm:block">{settings?.site_title?.split(' ').map(w => w[0]).join('').slice(0, 6) || 'ABMHC'}</span>
        </button>
        <div className="hidden md:flex items-center gap-1">
          {navItems.map(n => (
            <button
              key={n.id}
              onClick={() => setSection(n.id)}
              className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                n.highlight ? 'bg-blue-700 text-white hover:bg-blue-800' :
                currentSection === n.id ? 'bg-blue-50 text-blue-700 font-medium' :
                'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid={`nav-${n.id}`}
            >{n.label}</button>
          ))}
        </div>
        <button className="md:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {mobileOpen && (
        <div className="md:hidden bg-white border-t p-3 space-y-1">
          {navItems.map(n => (
            <button key={n.id} onClick={() => { setSection(n.id); setMobileOpen(false); }}
              className={`block w-full text-left px-3 py-2 rounded text-sm ${
                n.highlight ? 'bg-blue-700 text-white' :
                currentSection === n.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-600 hover:bg-slate-100'
              }`}>{n.label}</button>
          ))}
        </div>
      )}
    </nav>
  );
};

// ==================== HOME ====================
const HomeSection = ({ setSection }) => {
  const { data: stats } = useQuery({ queryKey: ['portal-stats'], queryFn: () => pubApi('/stats') });
  const { data: settings } = useQuery({ queryKey: ['portal-settings'], queryFn: () => pubApi('/settings') });
  const { data: milestones = [] } = useQuery({ queryKey: ['portal-milestones'], queryFn: () => pubApi('/milestones') });
  const { data: updates = [] } = useQuery({ queryKey: ['portal-updates'], queryFn: () => pubApi('/updates') });

  const heroImg = settings?.hero_image_url || 'https://customer-assets.emergentagent.com/job_bb47efb8-a204-403c-b115-242de9553cbc/artifacts/hu0ej963_IMG-20240415-WA0020.jpg';
  const inProgress = milestones.find(m => m.status === 'in_progress');

  return (
    <div data-testid="home-section">
      {/* Hero */}
      <div className="relative h-[70vh] min-h-[500px] overflow-hidden">
        <img src={heroImg} alt="ABMHC" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-950/80 via-blue-900/60 to-transparent" />
        <div className="relative z-10 max-w-6xl mx-auto px-4 h-full flex flex-col justify-center">
          {settings?.logo_url && (
            <img src={settings.logo_url} alt="Logo" className="h-16 w-auto object-contain mb-4" />
          )}
          <Badge className="bg-blue-600/80 text-white mb-4 w-fit">Healthcare for Rural Zimbabwe</Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white max-w-2xl leading-tight">
            {settings?.site_title || 'Archbishop Bartholomew Manjoro Health Centre'}
          </h1>
          <p className="text-lg text-blue-100 mt-4 max-w-xl">
            {settings?.mission_text?.slice(0, 150) || 'Building modern healthcare infrastructure for underserved rural communities.'}
          </p>
          <div className="flex gap-3 mt-8">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700" onClick={() => setSection('donate')} data-testid="hero-donate-btn">
              <Heart className="h-5 w-5 mr-2" /> Support Our Mission
            </Button>
            <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20" onClick={() => setSection('progress')}>
              View Progress <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="bg-blue-900 py-6">
          <div className="max-w-6xl mx-auto px-4 grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            <div><p className="text-3xl font-bold text-white">{stats.overall_progress}%</p><p className="text-blue-200 text-sm">Overall Progress</p></div>
            <div><p className="text-3xl font-bold text-white">${(stats.raised_amount || 0).toLocaleString()}</p><p className="text-blue-200 text-sm">Raised of ${(stats.goal_amount || 500000).toLocaleString()}</p></div>
            <div><p className="text-3xl font-bold text-white">{stats.milestones_completed}</p><p className="text-blue-200 text-sm">Milestones Completed</p></div>
            <div><p className="text-3xl font-bold text-white">{stats.donor_count}</p><p className="text-blue-200 text-sm">Generous Donors</p></div>
          </div>
        </div>
      )}

      {/* Funding Progress */}
      {stats && stats.goal_amount > 0 && (
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-8 border border-blue-100">
            <h2 className="text-lg font-bold text-blue-900 mb-2">Fundraising Progress</h2>
            <div className="flex items-end gap-2 mb-4">
              <span className="text-3xl font-bold text-blue-700">${(stats.raised_amount || 0).toLocaleString()}</span>
              <span className="text-slate-500 pb-1">of ${(stats.goal_amount || 0).toLocaleString()} goal</span>
            </div>
            <div className="w-full bg-blue-100 rounded-full h-4 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-400 h-full rounded-full transition-all duration-1000" style={{ width: `${Math.min(stats.funding_percent || 0, 100)}%` }} />
            </div>
            <p className="text-sm text-slate-500 mt-2">{stats.funding_percent || 0}% funded</p>
          </div>
        </div>
      )}

      {/* Current Milestone */}
      {inProgress && (
        <div className="max-w-6xl mx-auto px-4 pb-12">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Currently In Progress</h2>
          <div className="bg-white rounded-xl border p-6 flex items-center gap-6 flex-wrap">
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
              <Activity className="h-8 w-8 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0 sm:min-w-[200px]">
              <h3 className="font-semibold text-lg">{inProgress.title}</h3>
              <p className="text-sm text-slate-500">{inProgress.description}</p>
              <div className="mt-2 w-full bg-blue-100 rounded-full h-2">
                <div className="bg-blue-600 h-full rounded-full" style={{ width: `${inProgress.progress_percent}%` }} />
              </div>
              <p className="text-xs text-slate-400 mt-1">{inProgress.progress_percent}% complete</p>
            </div>
            <Button variant="outline" onClick={() => setSection('progress')}>View All <ChevronRight className="h-4 w-4 ml-1" /></Button>
          </div>
        </div>
      )}

      {/* Impact Stats */}
      <div className="bg-slate-50 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-lg font-bold text-slate-900 text-center mb-8">Lives We Will Touch</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {[
              { n: '50,000+', l: 'People Served', icon: Users },
              { n: '15', l: 'Villages Covered', icon: MapPin },
              { n: '120', l: 'Hospital Beds', icon: Building2 },
              { n: '24/7', l: 'Emergency Care', icon: Shield },
            ].map(s => (
              <div key={s.l} className="text-center p-4 bg-white rounded-xl border">
                <s.icon className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                <p className="text-2xl font-bold text-slate-900">{s.n}</p>
                <p className="text-xs text-slate-500">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Latest Update */}
      {updates.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 py-12">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Latest Update</h2>
          <div className="bg-white rounded-xl border overflow-hidden flex flex-col sm:flex-row">
            {updates[0].image_url && (
              <img src={updates[0].image_url} alt="" className="sm:w-1/3 h-48 sm:h-auto object-cover" />
            )}
            <div className="p-6 flex-1">
              <h3 className="font-semibold text-lg mb-2">{updates[0].title}</h3>
              <p className="text-sm text-slate-600 line-clamp-3">{updates[0].summary || updates[0].content?.slice(0, 200)}</p>
              <p className="text-xs text-slate-400 mt-3">{new Date(updates[0].created_at).toLocaleDateString()}</p>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="bg-blue-900 py-16 text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white mb-4">Every Contribution Makes a Difference</h2>
          <p className="text-blue-200 mb-8">Your donation helps bring modern healthcare to thousands of families who currently have no access.</p>
          <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50" onClick={() => setSection('donate')}>
            <Heart className="h-5 w-5 mr-2 text-red-500" /> Donate Now
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== ABOUT ====================
const AboutSection = ({ setSection }) => {
  const { data: settings } = useQuery({ queryKey: ['portal-settings'], queryFn: () => pubApi('/settings') });
  const facilities = [
    { name: 'Emergency Department', desc: '24/7 emergency services with trauma care', icon: Shield },
    { name: 'Maternity Ward', desc: 'Safe delivery with prenatal and postnatal care', icon: Baby },
    { name: 'Diagnostic Center', desc: 'X-ray, ultrasound, and laboratory services', icon: Microscope },
    { name: 'Pharmacy', desc: 'Essential medicines and medical supplies', icon: Pill },
    { name: 'Outpatient Clinics', desc: 'General consultations and specialist referrals', icon: Stethoscope },
    { name: 'Pediatric Unit', desc: "Dedicated children's health services", icon: Heart },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-12 space-y-12" data-testid="about-section">
      <div className="max-w-3xl">
        <Badge className="bg-blue-100 text-blue-700 mb-3">About Us</Badge>
        <h1 className="text-3xl font-bold text-slate-900 mb-4">Building Healthcare Infrastructure for Rural Zimbabwe</h1>
        <p className="text-slate-600 leading-relaxed">{settings?.about_text || 'A community-driven initiative to bring modern medical facilities to underserved areas.'}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <img src="https://customer-assets.emergentagent.com/job_bb47efb8-a204-403c-b115-242de9553cbc/artifacts/005zx4lk_IMG-20240807-WA0007.jpg"
          alt="Community visit" className="rounded-xl object-cover h-64 w-full" />
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-slate-900">A Dream Born from Necessity</h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            In the rural districts of Zimbabwe, families often travel over 100 kilometers to reach the nearest hospital.
            The vision for this health centre emerged from the community itself — local leaders, healthcare workers, and
            international partners came together to build a modern hospital serving over 50,000 people.
          </p>
        </div>
      </div>

      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-6 text-center">Planned Facilities</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {facilities.map(f => (
            <div key={f.name} className="p-5 bg-white rounded-xl border hover:shadow-md transition-shadow">
              <f.icon className="h-8 w-8 text-blue-600 mb-3" />
              <h3 className="font-semibold text-sm">{f.name}</h3>
              <p className="text-xs text-slate-500 mt-1">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="text-center py-8">
        <h2 className="text-xl font-bold text-slate-900 mb-4">Be Part of This Journey</h2>
        <p className="text-slate-500 mb-6">Every contribution brings us closer to opening day.</p>
        <div className="flex gap-3 justify-center">
          <Button className="bg-blue-700" onClick={() => setSection('donate')}><Heart className="h-4 w-4 mr-2" /> Make a Donation</Button>
          <Button variant="outline" onClick={() => setSection('progress')}>View Progress</Button>
        </div>
      </div>
    </div>
  );
};

// ==================== PROGRESS / MILESTONES ====================
const ProgressSection = () => {
  const { data: milestones = [] } = useQuery({ queryKey: ['portal-milestones'], queryFn: () => pubApi('/milestones') });
  const { data: updates = [] } = useQuery({ queryKey: ['portal-updates'], queryFn: () => pubApi('/updates') });
  const { data: stats } = useQuery({ queryKey: ['portal-stats'], queryFn: () => pubApi('/stats') });

  const statusColors = { completed: 'bg-green-100 text-green-700', in_progress: 'bg-blue-100 text-blue-700', planned: 'bg-slate-100 text-slate-600' };

  return (
    <div className="max-w-6xl mx-auto px-4 py-12 space-y-10" data-testid="progress-section">
      <div>
        <Badge className="bg-blue-100 text-blue-700 mb-3">Construction Progress</Badge>
        <h1 className="text-3xl font-bold text-slate-900">Project Milestones</h1>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="p-4 bg-blue-50 rounded-xl text-center"><p className="text-3xl font-bold text-blue-700">{stats.overall_progress}%</p><p className="text-xs text-slate-500">Overall</p></div>
          <div className="p-4 bg-green-50 rounded-xl text-center"><p className="text-3xl font-bold text-green-700">{stats.milestones_completed}</p><p className="text-xs text-slate-500">Completed</p></div>
          <div className="p-4 bg-amber-50 rounded-xl text-center"><p className="text-3xl font-bold text-amber-700">{stats.milestones_in_progress}</p><p className="text-xs text-slate-500">In Progress</p></div>
          <div className="p-4 bg-slate-50 rounded-xl text-center"><p className="text-3xl font-bold text-slate-700">{stats.milestones_total - stats.milestones_completed - stats.milestones_in_progress}</p><p className="text-xs text-slate-500">Planned</p></div>
        </div>
      )}

      {/* Milestone Timeline */}
      <div className="relative pl-8">
        <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-slate-200" />
        {milestones.map((m, idx) => (
          <div key={m.id} className="relative mb-8 last:mb-0" data-testid={`milestone-${idx}`}>
            <div className={`absolute -left-8 top-0 w-8 h-8 rounded-full flex items-center justify-center ${
              m.status === 'completed' ? 'bg-green-500 text-white' :
              m.status === 'in_progress' ? 'bg-blue-500 text-white' :
              'bg-slate-200 text-slate-500'
            }`}>
              {m.status === 'completed' ? <CheckCircle2 className="h-4 w-4" /> : <span className="text-xs font-bold">{idx + 1}</span>}
            </div>
            <div className="pl-4 bg-white rounded-xl border p-5">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 font-medium">Phase {idx + 1}</span>
                  <Badge className={statusColors[m.status] || statusColors.planned}>{m.status.replace('_', ' ')}</Badge>
                </div>
                <span className="text-sm font-bold text-blue-700">{m.progress_percent}%</span>
              </div>
              <h3 className="font-semibold text-base">{m.title}</h3>
              <p className="text-sm text-slate-500 mt-1">{m.description}</p>
              {m.progress_percent > 0 && m.progress_percent < 100 && (
                <div className="mt-3 w-full bg-blue-100 rounded-full h-2">
                  <div className="bg-blue-600 h-full rounded-full" style={{ width: `${m.progress_percent}%` }} />
                </div>
              )}
              {(m.start_date || m.end_date) && (
                <div className="mt-2 flex gap-4 text-xs text-slate-400">
                  {m.start_date && <span><Calendar className="h-3 w-3 inline mr-1" />Started: {m.start_date}</span>}
                  {m.end_date && <span>Completed: {m.end_date}</span>}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Updates */}
      {updates.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-slate-900 mb-4">Project Updates</h2>
          <div className="space-y-4">
            {updates.map(u => (
              <div key={u.id} className="bg-white rounded-xl border p-5 flex gap-4 flex-col sm:flex-row">
                {u.image_url && <img src={u.image_url} alt="" className="w-full sm:w-32 h-32 sm:h-24 object-cover rounded-lg shrink-0" />}
                <div>
                  <h3 className="font-semibold text-sm">{u.title}</h3>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{u.summary || u.content?.slice(0, 200)}</p>
                  <div className="flex gap-2 mt-2">
                    {(u.tags || []).map(t => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                    <span className="text-[10px] text-slate-400 ml-auto">{new Date(u.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== GALLERY ====================
const GallerySection = () => {
  const { data: galleryData } = useQuery({ queryKey: ['portal-gallery'], queryFn: () => pubApi('/gallery') });
  const [filter, setFilter] = useState('');
  const media = galleryData?.media || [];
  const albums = galleryData?.albums || [];
  const filtered = filter ? media.filter(m => m.album === filter) : media;

  return (
    <div className="max-w-6xl mx-auto px-4 py-12 space-y-6" data-testid="gallery-section">
      <div>
        <Badge className="bg-blue-100 text-blue-700 mb-3">Gallery</Badge>
        <h1 className="text-3xl font-bold text-slate-900">Construction Progress Photos</h1>
      </div>

      {albums.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setFilter('')} className={`px-3 py-1.5 rounded-full text-sm border ${!filter ? 'bg-blue-100 border-blue-300 text-blue-700' : 'bg-white border-slate-200'}`}>All ({media.length})</button>
          {albums.map(a => (
            <button key={a} onClick={() => setFilter(a)} className={`px-3 py-1.5 rounded-full text-sm border ${filter === a ? 'bg-blue-100 border-blue-300 text-blue-700' : 'bg-white border-slate-200'}`}>
              {a} ({media.filter(m => m.album === a).length})
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(m => (
          <div key={m.id} className="group relative rounded-xl overflow-hidden border bg-slate-100 aspect-[4/3]">
            <img src={m.media_url} alt={m.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <p className="text-white text-sm font-medium">{m.title}</p>
                {m.album && <Badge className="bg-white/20 text-white text-[10px] mt-1">{m.album}</Badge>}
              </div>
            </div>
            {m.is_featured && <Badge className="absolute top-2 right-2 bg-blue-600 text-white text-[10px]">Featured</Badge>}
          </div>
        ))}
      </div>
      {filtered.length === 0 && <p className="text-center text-slate-400 py-12">No media in this album</p>}
    </div>
  );
};

// ==================== DONATE ====================
const DonateSection = () => {
  const { data: stats } = useQuery({ queryKey: ['portal-stats'], queryFn: () => pubApi('/stats') });
  const { data: recent = [] } = useQuery({ queryKey: ['portal-recent-donations'], queryFn: () => pubApi('/donations/recent') });

  const [amount, setAmount] = useState(100);
  const [donationType, setDonationType] = useState('one_time');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [donorVisibility, setDonorVisibility] = useState('anonymous');
  const [submitted, setSubmitted] = useState(false);

  const donateMutation = useMutation({
    mutationFn: (data) => submitDonation({ tenantId: TENANT_ID, payload: data }),
    onSuccess: (d) => {
      if (d.mode === 'gateway') return; // redirecting to the payment gateway
      setSubmitted(true);
      toast.success('Gift recorded — we will confirm it once your payment arrives');
    },
    onError: (e) => toast.error(e.message || 'Donation failed. Please try again.'),
  });

  const presets = [25, 50, 100, 250, 500, 1000];

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="donation-success">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="h-10 w-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-3">Thank You for Your Generosity!</h2>
        <p className="text-slate-600 mb-6" data-testid="donation-pending-note">
          Your gift of <strong>${amount.toLocaleString()}</strong> has been recorded and is
          marked pending. We confirm it — and send your receipt — as soon as the payment is
          received.
        </p>
        <Button onClick={() => { setSubmitted(false); setAmount(100); setName(''); setEmail(''); setMessage(''); }}>Make Another Donation</Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-12" data-testid="donate-section">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Form */}
        <div className="lg:col-span-3 space-y-6">
          <div>
            <Badge className="bg-blue-100 text-blue-700 mb-3">Make a Donation</Badge>
            <h1 className="text-3xl font-bold text-slate-900">Support Our Mission</h1>
            <p className="text-slate-500 mt-2">Every dollar brings modern healthcare closer to 50,000+ people in rural Zimbabwe.</p>
          </div>

          {/* Donation Type */}
          <div className="flex gap-2">
            {[{ v: 'one_time', l: 'One-Time' }, { v: 'monthly', l: 'Monthly' }, { v: 'weekly', l: 'Weekly' }].map(t => (
              <button key={t.v} onClick={() => setDonationType(t.v)}
                className={`px-4 py-2 rounded-lg text-sm border font-medium transition-colors ${
                  donationType === t.v ? 'bg-blue-700 text-white border-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                }`} data-testid={`type-${t.v}`}>{t.l}</button>
            ))}
          </div>

          {/* Amount Presets */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Select Amount (USD)</Label>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
              {presets.map(p => (
                <button key={p} onClick={() => setAmount(p)}
                  className={`py-3 rounded-lg border text-sm font-semibold transition-colors ${
                    amount === p ? 'bg-blue-700 text-white border-blue-700' : 'bg-white border-slate-200 hover:border-blue-300'
                  }`} data-testid={`amount-${p}`}>${p}</button>
              ))}
            </div>
            <div className="relative">
              <DollarSign className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input type="number" className="pl-8" value={amount} onChange={e => setAmount(Number(e.target.value))} data-testid="custom-amount" />
            </div>
          </div>

          {/* Donor Info */}
          <div className="space-y-3">
            {/* Donor Visibility Selection */}
            <div>
              <Label className="text-sm font-medium mb-2 block">How would you like to appear?</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: 'public', l: 'Public', desc: 'Show my full name' },
                  { v: 'partial', l: 'Partial', desc: 'First name + initial' },
                  { v: 'anonymous', l: 'Anonymous', desc: 'Show as Anonymous' },
                  { v: 'private', l: 'Private', desc: 'Don\'t show at all' },
                ].map(opt => (
                  <button key={opt.v} onClick={() => {
                    setDonorVisibility(opt.v);
                    setIsAnonymous(opt.v === 'anonymous' || opt.v === 'private');
                  }}
                    className={`p-2.5 rounded-lg border text-left transition-all ${
                      donorVisibility === opt.v 
                        ? 'bg-blue-50 border-blue-400 ring-1 ring-blue-400' 
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`visibility-${opt.v}`}
                  >
                    <p className={`text-sm font-medium ${donorVisibility === opt.v ? 'text-blue-700' : 'text-slate-700'}`}>{opt.l}</p>
                    <p className="text-xs text-slate-500">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>
            {donorVisibility !== 'anonymous' && donorVisibility !== 'private' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" data-testid="donor-name" /></div>
                <div><Label>Email</Label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" data-testid="donor-email" /></div>
              </div>
            )}
            {(donorVisibility === 'anonymous' || donorVisibility === 'private') && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Name <span className="text-xs text-slate-400">(admin only)</span></Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name (not shown publicly)" data-testid="donor-name" /></div>
                <div><Label>Email <span className="text-xs text-slate-400">(for receipt)</span></Label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" data-testid="donor-email" /></div>
              </div>
            )}
            <div><Label>Phone <span className="text-xs text-slate-400">(optional, for contact)</span></Label><Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+263 77 123 4567" data-testid="donor-phone" /></div>
            <div><Label>Message (optional)</Label><Textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Leave a message of support..." rows={2} data-testid="donor-message" /></div>
          </div>

          <Button size="lg" className="w-full bg-blue-700 hover:bg-blue-800 h-12 text-base" onClick={() => donateMutation.mutate({ donor_name: name, donor_email: email, donor_phone: phone, amount, donation_type: donationType, message, is_anonymous: isAnonymous, donor_visibility: donorVisibility })} disabled={!amount || donateMutation.isPending} data-testid="submit-donation-btn">
            {donateMutation.isPending ? 'Processing...' : `Donate $${amount.toLocaleString()} ${donationType === 'monthly' ? '/month' : donationType === 'weekly' ? '/week' : ''}`}
          </Button>
          <p className="text-xs text-slate-500 text-center mt-2" data-testid="donate-payment-note">
            {getToken()
              ? 'Your gift is recorded as pending and confirmed once the payment reaches us.'
              : 'You will be taken to our secure card checkout to complete your gift.'}
          </p>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-2 space-y-6">
          {/* Impact */}
          <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
            <h3 className="font-semibold text-sm text-blue-900 mb-3">Your Impact</h3>
            <div className="space-y-2 text-sm text-blue-800">
              <p><strong>$25</strong> — Provides medical supplies for a day</p>
              <p><strong>$100</strong> — Funds one hospital bed</p>
              <p><strong>$500</strong> — Equips a consultation room</p>
              <p><strong>$1,000</strong> — Sponsors a ward section</p>
            </div>
          </div>

          {/* Recent Donors */}
          {recent.length > 0 && (
            <div className="bg-white rounded-xl p-5 border">
              <h3 className="font-semibold text-sm text-slate-900 mb-3">Recent Supporters</h3>
              <div className="space-y-3">
                {recent.slice(0, 5).map((d, i) => (
                  <div key={i} className="text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600 font-medium">{d.donor_name || 'Anonymous'}</span>
                      <span className="font-semibold text-blue-700">${d.amount}</span>
                    </div>
                    {d.message && <p className="text-xs text-slate-400 mt-0.5 italic">"{d.message}"</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Progress */}
          {stats && (
            <div className="bg-white rounded-xl p-5 border">
              <h3 className="font-semibold text-sm text-slate-900 mb-2">Fundraising Progress</h3>
              <div className="w-full bg-blue-100 rounded-full h-3 overflow-hidden mb-2">
                <div className="bg-blue-600 h-full rounded-full" style={{ width: `${Math.min(stats.funding_percent, 100)}%` }} />
              </div>
              <div className="flex justify-between text-xs text-slate-500">
                <span>${(stats.raised_amount || 0).toLocaleString()} raised</span>
                <span>${(stats.goal_amount || 0).toLocaleString()} goal</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== FOOTER ====================
const PortalFooter = ({ setSection }) => {
  const { data: settings } = useQuery({ queryKey: ['portal-settings'], queryFn: () => pubApi('/settings') });
  return (
    <footer className="bg-slate-900 text-slate-300 py-12">
      <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 sm:grid-cols-3 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-3">
            {settings?.logo_url ? (
              <img src={settings.logo_url} alt="Logo" className="h-8 w-auto object-contain" />
            ) : (
              <Building2 className="h-5 w-5 text-blue-400" />
            )}
            <span className="font-bold text-white">{settings?.site_title?.split(' ').map(w => w[0]).join('').slice(0, 6) || 'ABMHC'}</span>
          </div>
          <p className="text-sm text-slate-400">{settings?.site_subtitle || 'Building Healthcare for Rural Zimbabwe'}</p>
        </div>
        <div>
          <h4 className="font-semibold text-white mb-3 text-sm">Quick Links</h4>
          <div className="space-y-2 text-sm">
            {['home', 'about', 'progress', 'gallery', 'donate'].map(s => (
              <button key={s} onClick={() => setSection(s)} className="block text-slate-400 hover:text-white capitalize">{s}</button>
            ))}
          </div>
        </div>
        <div>
          <h4 className="font-semibold text-white mb-3 text-sm">Contact</h4>
          <div className="space-y-2 text-sm text-slate-400">
            {settings?.contact_email && <p className="flex items-center gap-2"><Mail className="h-3 w-3" />{settings.contact_email}</p>}
            {settings?.contact_phone && <p className="flex items-center gap-2"><Phone className="h-3 w-3" />{settings.contact_phone}</p>}
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 mt-8 pt-6 border-t border-slate-800 text-center text-xs text-slate-500">
        Archbishop Bartholomew Manjoro Health Centre. All rights reserved.
      </div>
    </footer>
  );
};

// ==================== MAIN ====================
const DonationPortalPage = () => {
  const [section, setSection] = useState('home');

  useEffect(() => { window.scrollTo(0, 0); }, [section]);

  const { data: portalSettings } = useQuery({ queryKey: ['portal-settings-nav'], queryFn: () => pubApi('/settings') });

  const renderSection = () => {
    switch (section) {
      case 'about': return <AboutSection setSection={setSection} />;
      case 'progress': return <ProgressSection />;
      case 'gallery': return <GallerySection />;
      case 'donate': return <DonateSection />;
      default: return <HomeSection setSection={setSection} />;
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="donation-portal">
      <PortalNav currentSection={section} setSection={setSection} settings={portalSettings} />
      {renderSection()}
      <PortalFooter setSection={setSection} />
    </div>
  );
};

export default DonationPortalPage;
